import { useId, useRef, useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";

import {
  clampTooltipShift,
  findClipBounds,
} from "./clampTooltipShift";
import style from "./InfoTooltip.module.css";

const TooltipBlock = ({ block }) => {
  if (block?.items) {
    return (
      <ul className={style.list}>
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  return <span className={style.paragraph}>{block}</span>;
};

const TooltipContent = ({ text }) => {
  if (!Array.isArray(text)) {
    return text;
  }

  return text.map((block, index) => (
    <TooltipBlock key={block?.items ? `list-${index}` : block} block={block} />
  ));
};

export const InfoTooltip = ({ text, ariaLabel = "More information" }) => {
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const [shift, setShift] = useState(0);

  /*
   * Measured as the bubble opens rather than fixed in CSS, because how much room a trigger has
   * depends on where the grid put it, not on which field it belongs to.
   *
   * The bubble keeps its layout while hidden (it is `visibility: hidden`, not `display: none`), so
   * its width reads correctly here and the position is right on the first paint — no second pass,
   * no visible jump.
   */
  const keepBubbleInView = () => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;

    if (!trigger || !bubble) return;

    const { left, width } = trigger.getBoundingClientRect();

    setShift(
      clampTooltipShift({
        triggerCenterX: left + width / 2,
        bubbleWidth: bubble.offsetWidth,
        ...findClipBounds(bubble),
      })
    );
  };

  return (
    <span className={style.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={style.trigger}
        aria-label={ariaLabel}
        aria-describedby={tooltipId}
        onMouseEnter={keepBubbleInView}
        onFocus={keepBubbleInView}
      >
        <AiOutlineInfoCircle aria-hidden="true" />
      </button>
      <span
        ref={bubbleRef}
        role="tooltip"
        id={tooltipId}
        className={style.bubble}
        style={{ "--tooltip-shift": `${shift}px` }}
      >
        <TooltipContent text={text} />
      </span>
    </span>
  );
};
